// Settings → My account → Name color (#631, #698): pick a color from the palette, or Automatic (the color your account started with, a
// hash of your id). The palette is the same in every theme: a bold row, then the assorted grid, then Automatic. The color is saved on
// the account and is the same for everyone, in every server and every theme, and the server tells every open client at once. Palette
// colors only, never a free color. Only you change it. A color under 3:1 on the open theme gets a note (the others see it on their own).
import { useState } from "react";
import { NAME_ASSORTED, NAME_BOLD, NAME_LOW_CONTRAST, nameColorHash, normalizeNameColor, themeInfo, type Me } from "@bloom/shared";
import { api } from "../api.ts";
import { useColoredNames } from "../coloredNames.ts";
import { useTheme } from "../theme.ts";
import { SettingRow } from "./SettingsShell.tsx";

export function NameColorSetting({ me, refresh }: { me: Me; refresh: () => Promise<Me | null> }) {
  /** What Automatic is for me: the hash color of my id. */
  const auto = nameColorHash(me.id);
  const saved = normalizeNameColor(me.settings?.nameColor) ?? auto;
  /** The choice shows at once, while the server saves it. `undefined`: nothing in flight. */
  const [pending, setPending] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const colored = useColoredNames();
  const { resolved } = useTheme();
  const shown = pending === undefined ? saved : pending;
  const low = (NAME_LOW_CONTRAST[resolved] ?? []).includes(shown);

  /** `null` is Automatic: the server stores my hash color again. */
  const choose = async (next: string | null) => {
    const color = next ?? auto;
    if (color === shown) return;
    setPending(color);
    setError(null);
    try {
      await api("/me/settings", { method: "PUT", body: { nameColor: next } });
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't save.");
    } finally {
      setPending(undefined);
    }
  };

  const swatch = (hex: string) => (
    <button
      key={hex}
      type="button"
      role="radio"
      aria-checked={shown === hex}
      aria-label={`Color ${hex}`}
      data-name-color={hex}
      className={`us-name-color-swatch${shown === hex ? " on" : ""}`}
      style={{ background: hex }}
      onClick={() => void choose(hex)}
    />
  );

  return (
    <SettingRow
      label="Name color"
      wide
      help="The color of your name in chat, in every server and every theme. Everyone sees the same color, at once. Automatic is the color your account started with. Saved to your account."
    >
      <div className="us-name-color">
        <div className="us-name-color-preview" aria-live="polite">
          <span className="muted">Preview</span>
          <strong data-testid="name-color-preview" data-name-color={shown} style={{ color: shown }}>
            {me.name}
          </strong>
          {!colored && <span className="muted">Colorized names is off on this device (Appearance), so chat shows plain names here.</span>}
        </div>
        <div className="us-name-color-grid us-name-color-bold" role="radiogroup" aria-label="Name color, bold colors">
          {NAME_BOLD.map(swatch)}
        </div>
        <div className="us-name-color-grid" role="radiogroup" aria-label="Name color, more colors">
          {NAME_ASSORTED.map(swatch)}
        </div>
        <button type="button" role="radio" aria-checked={shown === auto} data-name-color="auto" className={`us-name-color-auto${shown === auto ? " on" : ""}`} onClick={() => void choose(null)}>
          Automatic
        </button>
        {low && (
          <div className="us-note" role="note" data-testid="name-color-low">
            This color is hard to read on {themeInfo(resolved).label} (under 3:1). People on a theme where it reads well see it fine.
          </div>
        )}
        {error && <div className="error us-note" role="alert">{error}</div>}
      </div>
    </SettingRow>
  );
}
